import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../utils/axiosUtil";
import MarkdownQuillEditor from "./MarkdownQuillEditor";
import RegionalScopeSelect from "../common/RegionalScopeSelect";

// ─── helpers ────────────────────────────────────────────────────────────────
const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");



const CONTENT_TYPES = [
  { value: "text", label: "Text (single block)" },
  { value: "bullets", label: "Bullets (numbered list)" },
  { value: "paragraphs", label: "Paragraphs" },
  { value: "subheading_paragraphs", label: "Sub-heading + Paragraph" },
  { value: "companies", label: "Companies / Key Players" },
  { value: "json_recent_dev", label: "Recent Developments (timeline)" },
];

const emptyContent = (ct) => {
  if (ct === "text") return { text: "" };
  if (ct === "bullets") return { bullets: [""] };
  if (ct === "paragraphs") return { paragraphs: [""] };
  if (ct === "subheading_paragraphs") return { items: [{ subHeading: "", paragraph: "" }] };
  if (ct === "companies") return { companies: [{ company: "", summary: "" }] };
  if (ct === "json_recent_dev") return { items: [{ month: "", year: "", description: "" }] };
  return { text: "" };
};

const newSection = (seq) => ({
  _id: `${Date.now()}_${Math.random()}`,
  key: "",
  heading: "",
  sequence: seq,
  contentType: "text",
  _uiContentType: "text",
  content: { text: "" },
});

const newSegmentRow = () => ({ category: "", values: "" });

// ─── Section Content Editor ─────────────────────────────────────────────────
function SectionContentEditor({ section, onChange }) {
  const { _uiContentType: contentType, content } = section;
  const update = (patch) => onChange({ ...section, content: { ...content, ...patch } });

  if (contentType === "text") {
    return (
      <MarkdownQuillEditor
        value={content.text || ""}
        onChange={(val) => update({ text: val })}
        placeholder="Write the section text here. Supports **bold**, *italic*, headings, lists..."
        rows={8}
      />
    );
  }

  if (contentType === "bullets") {
    const bullets = content.bullets || [""];
    return (
      <div>
        {bullets.map((b, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-2">
            <span className="text-muted small" style={{ minWidth: 20, textAlign: "right" }}>{i + 1}.</span>
            <input
              className="form-control form-control-sm flex-grow-1"
              placeholder={`Bullet ${i + 1}`}
              value={b}
              onChange={(e) => { const n = [...bullets]; n[i] = e.target.value; update({ bullets: n }); }}
            />
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => update({ bullets: bullets.filter((_, j) => j !== i) })}>×</button>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary mt-1" onClick={() => update({ bullets: [...bullets, ""] })}>+ Add bullet</button>
      </div>
    );
  }

  if (contentType === "paragraphs") {
    const paragraphs = content.paragraphs || [""];
    return (
      <div>
        {paragraphs.map((p, i) => (
          <div key={i} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted fw-semibold">Paragraph {i + 1}</small>
              <button type="button" className="btn btn-sm btn-outline-danger" style={{ padding: "1px 8px", fontSize: 11 }}
                onClick={() => update({ paragraphs: paragraphs.filter((_, j) => j !== i) })}>Remove</button>
            </div>
            <MarkdownQuillEditor
              value={p}
              onChange={(val) => { const n = [...paragraphs]; n[i] = val; update({ paragraphs: n }); }}
              placeholder={`Paragraph ${i + 1}...`}
              rows={5}
            />
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
          onClick={() => update({ paragraphs: [...paragraphs, ""] })}>+ Add paragraph</button>
      </div>
    );
  }

  if (contentType === "companies") {
    const companies = content.companies || [{ company: "", summary: "" }];
    return (
      <div>
        {companies.map((c, i) => (
          <div key={i} className="border rounded p-3 mb-2" style={{ background: "#f9f9f9" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="fw-bold text-muted">Company {i + 1}</small>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => update({ companies: companies.filter((_, j) => j !== i) })}>Remove</button>
            </div>
            <input className="form-control form-control-sm mb-2" placeholder="Company name" value={c.company}
              onChange={(e) => { const n = [...companies]; n[i] = { ...c, company: e.target.value }; update({ companies: n }); }} />
            <textarea rows={2} className="form-control form-control-sm" placeholder="Short summary" value={c.summary}
              onChange={(e) => { const n = [...companies]; n[i] = { ...c, summary: e.target.value }; update({ companies: n }); }} />
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => update({ companies: [...companies, { company: "", summary: "" }] })}>+ Add company</button>
      </div>
    );
  }

  if (contentType === "json_recent_dev") {
    const items = content.items || [{ month: "", year: "", description: "" }];
    return (
      <div>
        {items.map((item, i) => (
          <div key={i} className="border rounded p-3 mb-2" style={{ background: "#f9f9f9" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="fw-bold text-muted">Event {i + 1}</small>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => update({ items: items.filter((_, j) => j !== i) })}>Remove</button>
            </div>
            <div className="d-flex gap-2 mb-2">
              <input className="form-control form-control-sm" placeholder="Month (e.g. January)" value={item.month}
                onChange={(e) => { const n = [...items]; n[i] = { ...item, month: e.target.value }; update({ items: n }); }} />
              <input className="form-control form-control-sm" placeholder="Year (e.g. 2025)" value={item.year}
                onChange={(e) => { const n = [...items]; n[i] = { ...item, year: e.target.value }; update({ items: n }); }} />
            </div>
            <textarea rows={2} className="form-control form-control-sm" placeholder="Description" value={item.description}
              onChange={(e) => { const n = [...items]; n[i] = { ...item, description: e.target.value }; update({ items: n }); }} />
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => update({ items: [...items, { month: "", year: "", description: "" }] })}>+ Add event</button>
      </div>
    );
  }

  if (contentType === "subheading_paragraphs") {
    const items = content.items || [{ subHeading: "", paragraph: "" }];
    return (
      <div>
        {items.map((item, i) => (
          <div key={i} className="border rounded p-3 mb-3" style={{ background: "#f8f9ff", borderColor: "#c7d2fe" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="fw-bold text-primary">Block {i + 1}</small>
              <button type="button" className="btn btn-sm btn-outline-danger"
                onClick={() => update({ items: items.filter((_, j) => j !== i) })}
              >Remove</button>
            </div>
            {/* Sub-heading */}
            <div className="mb-2">
              <label className="form-label small text-muted mb-1">Sub-heading <span className="fw-normal">(optional)</span></label>
              <input
                className="form-control form-control-sm fw-semibold"
                placeholder="e.g. Growing adoption of electric vehicles…"
                value={item.subHeading}
                onChange={(e) => {
                  const n = [...items];
                  n[i] = { ...item, subHeading: e.target.value };
                  update({ items: n });
                }}
              />
            </div>
            {/* Paragraph */}
            <div>
              <label className="form-label small text-muted mb-1">Paragraph</label>
              <MarkdownQuillEditor
                value={item.paragraph}
                onChange={(val) => {
                  const n = [...items];
                  n[i] = { ...item, paragraph: val };
                  update({ items: n });
                }}
                placeholder="Write paragraph content here..."
                rows={5}
              />
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary"
          onClick={() => update({ items: [...items, { subHeading: "", paragraph: "" }] })}
        >+ Add sub-heading block</button>
      </div>
    );
  }

  return null;
}

// ─── Accordion wrapper ──────────────────────────────────────────────────────
function Accordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg mb-3 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none"
      >
        <span className="text-lg font-semibold text-gray-900">{title}</span>
        <svg className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? "" : "hidden"}`}>
        <div className="p-4 pt-2">{children}</div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function CreateManualReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || null; // from ReportRegistry

  const [title, setTitle] = useState("");
  const [marketName, setMarketName] = useState("");
  const [industry, setIndustry] = useState("");
  const [customIndustry, setCustomIndustry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [playersText, setPlayersText] = useState("");
  const [regionalScope, setRegionalScope] = useState("global");

  const [km, setKm] = useState({
    baseYear: new Date().getFullYear() - 1,
    forecastYear: new Date().getFullYear() + 9,
    forecastYearStart: new Date().getFullYear(),
    forecastPeriod: "",
    historicalMarketSize: { value: "", unit: "Billion", formatted: "" },
    marketSize: { value: "", unit: "Billion", formatted: "" },
    projectedMarketSize: { value: "", unit: "Billion", formatted: "" },
    cagr: { value: "", formatted: "" },
    dominantRegion: { region: "", sharePercent: "", formatted: "" },
    fastestGrowingRegion: { region: "", cagrPercent: "", formatted: "" },
  });

  // Regional shares for the regional map (stored like AI reports: section key "regional_analysis", content.regions[])
  const [regions, setRegions] = useState([
    { name: "North America", sharePercent: "", analysis: "" },
    { name: "Europe", sharePercent: "", analysis: "" },
    { name: "Asia-Pacific", sharePercent: "", analysis: "" },
    { name: "Latin America", sharePercent: "", analysis: "" },
    { name: "Middle East & Africa", sharePercent: "", analysis: "" },
  ]);
  const updateRegion = (idx, patch) =>
    setRegions((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const regionTotal = regions.reduce((s, r) => s + (Number(r.sharePercent) || 0), 0);
  const [regionalWriteup, setRegionalWriteup] = useState("");

  const [segmentRows, setSegmentRows] = useState([newSegmentRow()]);
  const [segmentGraph, setSegmentGraph] = useState({ selectedType: "", percentages: {} });
  const [sections, setSections] = useState([newSection(1)]);
  const [faqs, setFaqs] = useState([{ question: "", answer: "" }]);
  const [seo, setSeo] = useState({ metaTitle: "", metaDescription: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load industries
  useEffect(() => {
    axiosInstance.get("/industries").then(({ data }) => setIndustries(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Apply prefill from registry page (runs once after industries load)
  useEffect(() => {
    if (!prefill) return;
    if (prefill.title) setTitle(prefill.title);
    if (prefill.marketName) setMarketName(prefill.marketName);
    if (prefill.players && prefill.players.length) setPlayersText(prefill.players.join("\n"));
    // Segments from registry: { "By Type": [...], "By Application": [...] }
    if (prefill.segments && typeof prefill.segments === "object") {
      const rows = Object.entries(prefill.segments).map(([cat, vals]) => ({
        category: cat,
        values: Array.isArray(vals) ? vals.join("\n") : String(vals),
      }));
      if (rows.length) setSegmentRows(rows);
    }
    // Industry: try to match against loaded industries list
    if (prefill.industry) {
      setIndustry(prefill.industry);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, industries.length]);

  const setKmField = (path, value) => {
    setKm((prev) => {
      const copy = { ...prev };
      if (path.length === 1) copy[path[0]] = value;
      if (path.length === 2) copy[path[0]] = { ...copy[path[0]], [path[1]]: value };
      return copy;
    });
  };

  const addSection = () => setSections((prev) => [...prev, newSection(prev.length + 1)]);

  const removeSection = (idx) =>
    setSections((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sequence: i + 1 })));

  const moveSection = (idx, dir) => {
    setSections((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((s, i) => ({ ...s, sequence: i + 1 }));
    });
  };

  const updateSection = (idx, patch) =>
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const changeSectionContentType = (idx, uiCt) => {
    // json_recent_dev is stored as "json" on the backend; all other types store as-is
    const backendType = uiCt === "json_recent_dev" ? "json" : uiCt;
    setSections((prev) =>
      prev.map((s, i) =>
        i === idx
          ? { ...s, contentType: backendType, _uiContentType: uiCt, content: emptyContent(uiCt) }
          : s
      )
    );
  };

  const buildKeyMetrics = () => {
    const baseYear = Number(km.baseYear);
    const forecastYear = Number(km.forecastYear);
    const forecastYearStart = Number(km.forecastYearStart);
    return {
      baseYear,
      forecastYear,
      forecastYearStart,
      forecastPeriod: km.forecastPeriod || `${forecastYearStart}-${forecastYear}`,
      historicalMarketSize: km.historicalMarketSize.value
        ? { value: Number(km.historicalMarketSize.value) || 0, unit: km.historicalMarketSize.unit, formatted: km.historicalMarketSize.formatted || `USD ${km.historicalMarketSize.value} ${km.historicalMarketSize.unit}` }
        : null,
      marketSize: { value: Number(km.marketSize.value) || 0, unit: km.marketSize.unit, formatted: km.marketSize.formatted || `USD ${km.marketSize.value} ${km.marketSize.unit}` },
      projectedMarketSize: { value: Number(km.projectedMarketSize.value) || 0, unit: km.projectedMarketSize.unit, formatted: km.projectedMarketSize.formatted || `USD ${km.projectedMarketSize.value} ${km.projectedMarketSize.unit}` },
      cagr: { value: Number(km.cagr.value) || 0, formatted: km.cagr.formatted || `${km.cagr.value}%`, period: km.forecastPeriod || `${forecastYearStart}-${forecastYear}` },
      dominantRegion: { region: km.dominantRegion.region, sharePercent: Number(km.dominantRegion.sharePercent) || 0, formatted: km.dominantRegion.formatted || `${km.dominantRegion.region} (${km.dominantRegion.sharePercent}% Share)` },
      fastestGrowingRegion: { region: km.fastestGrowingRegion.region, cagrPercent: Number(km.fastestGrowingRegion.cagrPercent) || 0, formatted: km.fastestGrowingRegion.formatted || `${km.fastestGrowingRegion.region} (${km.fastestGrowingRegion.cagrPercent}% CAGR)` },
    };
  };

  const buildSections = () => {
    const built = sections
      .filter((s) => s.heading.trim())
      .map((s, i) => ({
        key: s.key.trim() || slugify(s.heading) || `section_${i + 1}`,
        heading: s.heading.trim(),
        sequence: i + 1,
        contentType: s.contentType,
        content: s.content,
      }));
    // Same shape the AI generator stores for the regional map
    const validRegions = regions
      .filter((r) => r.name.trim() && r.sharePercent !== "" && !isNaN(Number(r.sharePercent)))
      .map((r) => ({ name: r.name.trim(), sharePercent: Number(r.sharePercent), analysis: r.analysis.trim() }));
    if (validRegions.length > 0 || regionalWriteup.trim()) {
      const idx = built.findIndex((s) => s.key === "regional_analysis");
      const existing = idx >= 0 ? built[idx] : null;
      const mergedContent = {
        ...(existing?.content || {}),
        ...(regionalWriteup.trim() ? { text: regionalWriteup.trim() } : {}),
        ...(validRegions.length > 0 ? { regions: validRegions } : {}),
      };
      const regionalSection = {
        key: "regional_analysis",
        heading: existing ? existing.heading : "Regional Analysis",
        sequence: existing ? existing.sequence : built.length + 1,
        contentType: existing?.contentType || "json",
        content: mergedContent,
      };
      if (idx >= 0) built[idx] = regionalSection;
      else built.push(regionalSection);
    }
    return built;
  };

  // Build structuredSegments for backward-compat (TOC, table of figures, etc.)
  const buildSegments = () => {
    const obj = {};
    segmentRows.forEach(({ category, values }) => {
      if (category.trim())
        obj[category.trim()] = values.split("\n").map((v) => v.trim()).filter(Boolean);
    });
    return obj;
  };

  // Build a pie chart entry from the selected segment type + its percentages
  const buildSegmentChart = () => {
    const { selectedType, percentages } = segmentGraph;
    if (!selectedType) return null;
    const row = segmentRows.find((r) => r.category.trim() === selectedType);
    if (!row) return null;
    const subsegments = row.values.split(/[,\n]/).map((v) => v.trim()).filter(Boolean);
    if (subsegments.length === 0) return null;
    return {
      name: selectedType,
      type: "pie",
      labels: subsegments,
      series: subsegments.map((s) => Number(percentages[s]) || 0),
      meta: { segmentType: selectedType },
    };
  };

  const resolvedIndustry = industry === "__custom" ? customIndustry : industry;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!title.trim()) return setError("Title is required");
    if (!resolvedIndustry.trim()) return setError("Industry is required");
    const builtSections = buildSections();
    if (builtSections.length === 0) return setError("At least one section with a heading is required");

    try {
      setSubmitting(true);
      const segmentChart = buildSegmentChart();
      const payload = {
        title: title.trim(),
        marketName: marketName.trim() || undefined,
        industry: resolvedIndustry.trim(),
        regionalScope,
        keyMetrics: buildKeyMetrics(),
        players: playersText.split(/[\n,]/).map((p) => p.trim()).filter(Boolean),
        structuredSegments: buildSegments(),
        segmentChart: segmentChart || undefined,
        sections: builtSections,
        faqs: faqs.filter((f) => f.question.trim() && f.answer.trim()),
        seoMetadata: seo.metaTitle ? seo : undefined,
      };
      const { data } = await axiosInstance.post("/reports/custom", payload);

      // Mark registry entry as generated if we came from there
      if (prefill?.registryId) {
        try {
          await axiosInstance.patch(`/report-registry/${prefill.registryId}/mark-generated`, {
            reportId: data._id,
            type: "manual",
          });
        } catch (_) { /* non-fatal */ }
      }

      setSuccess(`Report created! Slug: ${data.slug}. Redirecting...`);
      setTimeout(() => navigate("/admin/reports/archive"), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6" }}>
      {/* Sticky Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="button" onClick={() => navigate("/admin/reports/archive")} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}>
              <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>Create Manual Report</h1>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? "Creating..." : "Create Report"}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: 900, margin: "0 auto", padding: "24px 20px" }}>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* 1. Basic Info */}
        <Accordion title="1. Basic Info" defaultOpen>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Report Title *</label>
              <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Global EV Battery Market Insights 2026-2035" />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Market Name <span className="text-muted fw-normal">(optional)</span></label>
              <input className="form-control" value={marketName} onChange={(e) => setMarketName(e.target.value)} placeholder="e.g. Global EV Battery Market" />
              <small className="text-muted">Auto-derived from title if blank</small>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Regional Scope *</label>
              <RegionalScopeSelect
                value={regionalScope}
                onChange={(val) => setRegionalScope(val)}
                id="regionalScope"
              />
              <small className="text-muted">Saved as-is; no auto-detection applied.</small>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Industry *</label>
              <select className="form-control" value={industry} onChange={(e) => setIndustry(e.target.value)}>
                <option value="">-- Select Industry --</option>
                {industries.map((ind) => <option key={ind._id} value={ind.name}>{ind.name}</option>)}
                <option value="__custom">Other (type below)</option>
              </select>
              {industry === "__custom" && (
                <input className="form-control mt-2" placeholder="Type industry name" value={customIndustry} onChange={(e) => setCustomIndustry(e.target.value)} />
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Key Players <span className="text-muted fw-normal">(one per line or comma-separated)</span></label>
              <textarea rows={5} className="form-control" placeholder={"Company A\nCompany B\nCompany C or Company A, Company B"} value={playersText} onChange={(e) => setPlayersText(e.target.value)} />
            </div>
          </div>
        </Accordion>

        {/* 2. Key Metrics */}
        <Accordion title="2. Key Metrics">
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Base Year</label>
              <input type="number" className="form-control form-control-sm" value={km.baseYear} onChange={(e) => setKmField(["baseYear"], e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Forecast Start</label>
              <input type="number" className="form-control form-control-sm" value={km.forecastYearStart} onChange={(e) => setKmField(["forecastYearStart"], e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Forecast End</label>
              <input type="number" className="form-control form-control-sm" value={km.forecastYear} onChange={(e) => setKmField(["forecastYear"], e.target.value)} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Forecast Period</label>
              <input className="form-control form-control-sm" placeholder="e.g. 2026-2035" value={km.forecastPeriod} onChange={(e) => setKmField(["forecastPeriod"], e.target.value)} />
            </div>
          </div>
          <div className="row g-3">
            {/* Historical Market Size — explicit 3rd chart point */}
            <div className="col-md-4">
              <div className="card card-body bg-light h-100" style={{ borderLeft: "3px solid #6b7280" }}>
                <p className="small fw-bold mb-1" style={{ color: "#374151" }}>
                  Historical Market Size
                  <span className="ms-1 fw-normal text-muted" style={{ fontSize: 11 }}>({km.baseYear} — chart point 1)</span>
                </p>
                <small className="text-muted d-block mb-2" style={{ fontSize: 11 }}>
                  If left blank, auto-calculated from current value ÷ (1 + CAGR%).
                </small>
                <input className="form-control form-control-sm mb-1" type="number" step="0.01" placeholder={`Value (e.g. ${km.marketSize.value ? (Number(km.marketSize.value) / (1 + (Number(km.cagr.value) || 8) / 100)).toFixed(2) : "19.5"})`} value={km.historicalMarketSize.value} onChange={(e) => setKmField(["historicalMarketSize", "value"], e.target.value)} />
                <select className="form-control form-control-sm mb-1" value={km.historicalMarketSize.unit} onChange={(e) => setKmField(["historicalMarketSize", "unit"], e.target.value)}>
                  <option value="Billion">Billion</option><option value="Million">Million</option><option value="Trillion">Trillion</option>
                </select>
                <input className="form-control form-control-sm" placeholder="Formatted (e.g. USD 19.5 Billion)" value={km.historicalMarketSize.formatted} onChange={(e) => setKmField(["historicalMarketSize", "formatted"], e.target.value)} />
              </div>
            </div>
            {/* Current Market Size */}
            <div className="col-md-4">
              <div className="card card-body bg-light h-100" style={{ borderLeft: "3px solid #2563eb" }}>
                <p className="small fw-bold text-primary mb-1">
                  Current Market Size
                  <span className="ms-1 fw-normal text-muted" style={{ fontSize: 11 }}>({Number(km.baseYear) + 1} — chart point 2)</span>
                </p>
                <input className="form-control form-control-sm mb-1" type="number" step="0.01" placeholder="Value (e.g. 21.2)" value={km.marketSize.value} onChange={(e) => setKmField(["marketSize", "value"], e.target.value)} />
                <select className="form-control form-control-sm mb-1" value={km.marketSize.unit} onChange={(e) => setKmField(["marketSize", "unit"], e.target.value)}>
                  <option value="Billion">Billion</option><option value="Million">Million</option><option value="Trillion">Trillion</option>
                </select>
                <input className="form-control form-control-sm" placeholder="Formatted (e.g. USD 21.2 Billion)" value={km.marketSize.formatted} onChange={(e) => setKmField(["marketSize", "formatted"], e.target.value)} />
              </div>
            </div>
            {/* Projected */}
            <div className="col-md-4">
              <div className="card card-body bg-light h-100" style={{ borderLeft: "3px solid #16a34a" }}>
                <p className="small fw-bold text-success mb-1">
                  Projected Market Size
                  <span className="ms-1 fw-normal text-muted" style={{ fontSize: 11 }}>({km.forecastYear} — chart point 3)</span>
                </p>
                <input className="form-control form-control-sm mb-1" type="number" step="0.01" placeholder="Value (e.g. 46.6)" value={km.projectedMarketSize.value} onChange={(e) => setKmField(["projectedMarketSize", "value"], e.target.value)} />
                <select className="form-control form-control-sm mb-1" value={km.projectedMarketSize.unit} onChange={(e) => setKmField(["projectedMarketSize", "unit"], e.target.value)}>
                  <option value="Billion">Billion</option><option value="Million">Million</option><option value="Trillion">Trillion</option>
                </select>
                <input className="form-control form-control-sm" placeholder="Formatted (e.g. USD 46.6 Billion)" value={km.projectedMarketSize.formatted} onChange={(e) => setKmField(["projectedMarketSize", "formatted"], e.target.value)} />
              </div>
            </div>
            {/* CAGR */}
            <div className="col-md-4">
              <div className="card card-body bg-light h-100">
                <p className="small fw-bold text-warning mb-2">CAGR</p>
                <input className="form-control form-control-sm mb-1" type="number" step="0.01" placeholder="Value (e.g. 8.2)" value={km.cagr.value} onChange={(e) => setKmField(["cagr", "value"], e.target.value)} />
                <input className="form-control form-control-sm" placeholder="Formatted (e.g. 8.2%)" value={km.cagr.formatted} onChange={(e) => setKmField(["cagr", "formatted"], e.target.value)} />
              </div>
            </div>
            {/* Dominant Region */}
            <div className="col-md-6">
              <div className="card card-body bg-light">
                <p className="small fw-bold text-purple mb-2" style={{ color: "#7c3aed" }}>Dominant Region</p>
                <input className="form-control form-control-sm mb-1" placeholder="Region (e.g. Asia-Pacific)" value={km.dominantRegion.region} onChange={(e) => setKmField(["dominantRegion", "region"], e.target.value)} />
                <input className="form-control form-control-sm mb-1" type="number" placeholder="Share %" value={km.dominantRegion.sharePercent} onChange={(e) => setKmField(["dominantRegion", "sharePercent"], e.target.value)} />
                <input className="form-control form-control-sm" placeholder="Formatted (e.g. Asia-Pacific (48% Share))" value={km.dominantRegion.formatted} onChange={(e) => setKmField(["dominantRegion", "formatted"], e.target.value)} />
              </div>
            </div>
            {/* Fastest Growing */}
            <div className="col-md-6">
              <div className="card card-body bg-light">
                <p className="small fw-bold mb-2" style={{ color: "#ea580c" }}>Fastest Growing Region</p>
                <input className="form-control form-control-sm mb-1" placeholder="Region (e.g. Asia-Pacific)" value={km.fastestGrowingRegion.region} onChange={(e) => setKmField(["fastestGrowingRegion", "region"], e.target.value)} />
                <input className="form-control form-control-sm mb-1" type="number" step="0.01" placeholder="CAGR %" value={km.fastestGrowingRegion.cagrPercent} onChange={(e) => setKmField(["fastestGrowingRegion", "cagrPercent"], e.target.value)} />
                <input className="form-control form-control-sm" placeholder="Formatted (e.g. Asia-Pacific (11.72% CAGR))" value={km.fastestGrowingRegion.formatted} onChange={(e) => setKmField(["fastestGrowingRegion", "formatted"], e.target.value)} />
              </div>
            </div>
          </div>
        </Accordion>

        {/* Regional Shares (for regional map) */}
        <Accordion title="Regional Shares for Map (optional)">
          <div className="mb-3">
            <label className="form-label small fw-semibold">Overall Regional Analysis Write-Up (optional)</label>
            <textarea
              rows={4}
              className="form-control form-control-sm"
              placeholder="Enter general regional analysis write-up / narrative covering key trends and dynamics..."
              value={regionalWriteup}
              onChange={(e) => setRegionalWriteup(e.target.value)}
            />
          </div>
          <p className="small text-muted">Share % for each region. Shown on the report's regional map. Total: <strong style={{ color: regionTotal > 100 ? "#dc2626" : undefined }}>{regionTotal.toFixed(1)}%</strong></p>
          {regions.map((r, i) => (
            <div key={i} className="row g-2 mb-2">
              <div className="col-md-4"><input className="form-control form-control-sm" placeholder="Region name" value={r.name} onChange={(e) => updateRegion(i, { name: e.target.value })} /></div>
              <div className="col-md-2"><input className="form-control form-control-sm" type="number" step="0.1" placeholder="Share %" value={r.sharePercent} onChange={(e) => updateRegion(i, { sharePercent: e.target.value })} /></div>
              <div className="col-md-6"><input className="form-control form-control-sm" placeholder="Short analysis (optional)" value={r.analysis} onChange={(e) => updateRegion(i, { analysis: e.target.value })} /></div>
            </div>
          ))}
        </Accordion>

        {/* 3. Market Segments + Graph */}
        <Accordion title="3. Market Segments (optional)">

          {/* ── Part A: All Segments ── */}
          <p className="text-muted small mb-2">Add all segment categories for this report. Values are comma-separated.</p>
          {segmentRows.map((row, i) => (
            <div key={i} className="d-flex gap-2 mb-2 align-items-center">
              <input
                className="form-control form-control-sm"
                style={{ width: "35%" }}
                placeholder="Category (e.g. By Technology)"
                value={row.category}
                onChange={(e) => { const n = [...segmentRows]; n[i] = { ...row, category: e.target.value }; setSegmentRows(n); }}
              />
              <textarea
                rows={4}
                className="form-control form-control-sm flex-grow-1"
                placeholder={"SoC\nMCU\nGPU\nFPGA"}
                value={row.values}
                onChange={(e) => { const n = [...segmentRows]; n[i] = { ...row, values: e.target.value }; setSegmentRows(n); }}
              />
              <button type="button" className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  setSegmentRows(segmentRows.filter((_, j) => j !== i));
                  // clear graph selection if deleted type was selected
                  if (segmentGraph.selectedType === row.category.trim())
                    setSegmentGraph({ selectedType: "", percentages: {} });
                }}
              >×</button>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
            onClick={() => setSegmentRows([...segmentRows, newSegmentRow()])}
          >+ Add segment category</button>

          {/* ── Part B: Segments Graph ── */}
          <hr className="my-3" />
          <p className="fw-semibold mb-1" style={{ fontSize: 14 }}>Segments Graph</p>
          <p className="text-muted small mb-3">
            Select one of the segment categories above, then enter the percentage for each of its subsegments.
            This will be saved as a pie chart on the report.
          </p>

          {/* Dropdown: populated from segmentRows */}
          <div className="mb-3">
            <label className="form-label small fw-semibold">Select Segment Type for Graph</label>
            <select
              className="form-control"
              value={segmentGraph.selectedType}
              onChange={(e) => setSegmentGraph({ selectedType: e.target.value, percentages: {} })}
            >
              <option value="">-- Select a segment type --</option>
              {segmentRows
                .filter((r) => r.category.trim())
                .map((r, i) => (
                  <option key={i} value={r.category.trim()}>{r.category.trim()}</option>
                ))
              }
            </select>
          </div>

          {/* Subsegment % inputs — auto-populated from selected row's values */}
          {segmentGraph.selectedType && (() => {
            const row = segmentRows.find((r) => r.category.trim() === segmentGraph.selectedType);
            const subsegments = row ? row.values.split(/[,\n]/).map((v) => v.trim()).filter(Boolean) : [];

            if (subsegments.length === 0) return (
              <p className="text-muted small">No values found for this segment. Add values in Part A above.</p>
            );

            const total = subsegments.reduce((acc, s) => acc + (Number(segmentGraph.percentages[s]) || 0), 0);
            const rounded = Math.round(total * 10) / 10;
            const perfect = Math.abs(rounded - 100) < 0.1;

            return (
              <div>
                <label className="form-label small fw-semibold mb-2">
                  Subsegment Percentages — <span className="text-muted fw-normal">{segmentGraph.selectedType}</span>
                </label>
                {subsegments.map((sub, i) => (
                  <div key={i} className="d-flex gap-2 mb-2 align-items-center">
                    <span className="text-muted small" style={{ minWidth: 22, textAlign: "right" }}>{i + 1}.</span>
                    <span className="form-control form-control-sm bg-light" style={{ flex: 2, color: "#374151" }}>{sub}</span>
                    <div className="input-group input-group-sm" style={{ flex: 1, maxWidth: 130 }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        className="form-control form-control-sm"
                        placeholder="%"
                        value={segmentGraph.percentages[sub] ?? ""}
                        onChange={(e) =>
                          setSegmentGraph((prev) => ({
                            ...prev,
                            percentages: { ...prev.percentages, [sub]: e.target.value },
                          }))
                        }
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                ))}

                {/* Total indicator */}
                <div className={`alert py-1 px-2 small mt-2 ${
                  perfect ? "alert-success" : rounded > 100 ? "alert-danger" : "alert-warning"
                }`}>
                  Total: <strong>{rounded}%</strong>
                  {perfect
                    ? " ✓ Adds up to 100%"
                    : rounded > 100
                    ? " — exceeds 100%"
                    : ` — ${Math.round((100 - rounded) * 10) / 10}% remaining`}
                </div>
              </div>
            );
          })()}
        </Accordion>

        {/* 4. Sections */}
        <Accordion title="4. Report Sections *" defaultOpen>
          <p className="text-muted small mb-3">Each section = one block on the public page. Use ↑↓ to reorder.</p>
          {sections.map((sec, idx) => (
            <div key={sec._id} className="card mb-3" style={{ borderColor: "#c7d2fe" }}>
              <div className="card-body">
                {/* Header row */}
                <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                  <span className="badge bg-primary" style={{ minWidth: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>{idx + 1}</span>
                  <input className="form-control form-control-sm flex-grow-1" style={{ minWidth: 180 }} placeholder="Section Heading (e.g. Executive Brief)"
                    value={sec.heading} onChange={(e) => updateSection(idx, { heading: e.target.value })} />
                  <select className="form-control form-control-sm" style={{ width: "auto", minWidth: 200 }}
                    value={sec._uiContentType || sec.contentType}
                    onChange={(e) => changeSectionContentType(idx, e.target.value)}>
                    {CONTENT_TYPES.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                  </select>
                  <div className="d-flex flex-column gap-1">
                    <button type="button" className="btn btn-xs btn-outline-secondary" style={{ padding: "1px 6px", fontSize: 11 }} disabled={idx === 0} onClick={() => moveSection(idx, -1)}>↑</button>
                    <button type="button" className="btn btn-xs btn-outline-secondary" style={{ padding: "1px 6px", fontSize: 11 }} disabled={idx === sections.length - 1} onClick={() => moveSection(idx, 1)}>↓</button>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeSection(idx)}>Remove</button>
                </div>
                {/* Key override */}
                <div className="mb-3">
                  <label className="form-label small text-muted mb-0">Section Key (auto, or override)</label>
                  <input className="form-control form-control-sm" style={{ color: "#9ca3af", fontSize: 12 }}
                    placeholder={sec.heading ? slugify(sec.heading) : "section_key"}
                    value={sec.key}
                    onChange={(e) => updateSection(idx, { key: e.target.value })} />
                </div>
                {/* Content editor */}
                <SectionContentEditor section={sec} onChange={(updated) => updateSection(idx, { content: updated.content })} />
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-outline-primary w-100" style={{ borderStyle: "dashed" }} onClick={addSection}>+ Add Section</button>
        </Accordion>

        {/* 5. FAQs */}
        <Accordion title="5. FAQs (optional)">
          {faqs.map((faq, i) => (
            <div key={i} className="card card-body bg-light mb-2">
              <div className="d-flex justify-content-between mb-2">
                <small className="fw-bold text-muted">FAQ {i + 1}</small>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFaqs(faqs.filter((_, j) => j !== i))}>Remove</button>
              </div>
              <input className="form-control form-control-sm mb-2" placeholder="Question" value={faq.question} onChange={(e) => { const n = [...faqs]; n[i] = { ...faq, question: e.target.value }; setFaqs(n); }} />
              <textarea rows={2} className="form-control form-control-sm" placeholder="Answer" value={faq.answer} onChange={(e) => { const n = [...faqs]; n[i] = { ...faq, answer: e.target.value }; setFaqs(n); }} />
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFaqs([...faqs, { question: "", answer: "" }])}>+ Add FAQ</button>
        </Accordion>

        {/* 6. SEO */}
        <Accordion title="6. SEO Metadata (optional)">
          <div className="mb-3">
            <label className="form-label fw-semibold">Meta Title</label>
            <input className="form-control" placeholder="Page title for search engines" value={seo.metaTitle} onChange={(e) => setSeo({ ...seo, metaTitle: e.target.value })} />
          </div>
          <div>
            <label className="form-label fw-semibold">Meta Description</label>
            <textarea rows={3} className="form-control" placeholder="Brief description for search engines (150-160 chars recommended)" value={seo.metaDescription} onChange={(e) => setSeo({ ...seo, metaDescription: e.target.value })} />
          </div>
        </Accordion>

        {/* Final submit */}
        <div className="pb-5">
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <button type="submit" disabled={submitting} className="btn btn-primary btn-lg w-100">
            {submitting ? "Creating Report..." : "Create Report"}
          </button>
        </div>
      </form>
    </div>
  );
}
